// @section 1 CardVoiceNote - record an audio voice note (MediaRecorder) with a
// live client-side transcript (Web Speech API, same engine as VoiceRecorder).
//
// Used by the card-capture flow: after photographing a business card, Adam
// narrates who the person was and the intended next step. The audio blob is
// archived to Drive server-side; the transcript is what downstream (review
// screen, research worker) actually reads, so it stays editable in the parent.
// If SpeechRecognition is unavailable the audio still records and the parent
// lets the operator type the note instead.

import { useCallback, useEffect, useRef, useState } from 'react';

type SpeechRecognitionResultLike = { 0: { transcript: string }; isFinal: boolean };
type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: { length: number; [i: number]: SpeechRecognitionResultLike };
};
type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

const MAX_SECONDS = 180;

export type VoiceNoteResult = { blob: Blob | null; durationMs: number; transcript: string };

// @section 2 Component
export default function CardVoiceNote({
  onNote,
  disabled,
}: {
  /** Called once when recording stops, with the audio blob + live transcript. */
  onNote: (note: VoiceNoteResult) => void;
  disabled?: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recogRef = useRef<SpeechRecognitionLike | null>(null);
  const finalRef = useRef('');
  const startedAtRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stoppingRef = useRef(false);

  const cleanupTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const stop = useCallback(() => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    recogRef.current?.stop();
    // onstop fires after the final dataavailable; the result is emitted there.
    if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
    cleanupTimer();
    setRecording(false);
  }, []);

  useEffect(
    () => () => {
      recogRef.current?.stop();
      if (mediaRef.current && mediaRef.current.state !== 'inactive') mediaRef.current.stop();
      mediaRef.current?.stream.getTracks().forEach((t) => t.stop());
      cleanupTimer();
    },
    [],
  );

  // @block 2.1 Start: mic stream -> MediaRecorder; SpeechRecognition in parallel.
  const start = useCallback(async () => {
    setError(null);
    setInterim('');
    finalRef.current = '';
    chunksRef.current = [];
    stoppingRef.current = false;
    setSeconds(0);

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone permission denied. Type the note instead.');
      return;
    }

    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = chunksRef.current.length
        ? new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' })
        : null;
      onNote({
        blob,
        durationMs: Date.now() - startedAtRef.current,
        transcript: finalRef.current.trim(),
      });
    };
    mediaRef.current = rec;

    // Live transcript alongside the recording (best effort - audio is the backup).
    const Ctor = getRecognitionCtor();
    if (Ctor) {
      const recog = new Ctor();
      recog.lang = 'en-US';
      recog.continuous = true;
      recog.interimResults = true;
      recog.onresult = (e) => {
        let live = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (r.isFinal) finalRef.current += r[0].transcript;
          else live += r[0].transcript;
        }
        setInterim(live);
      };
      recog.onerror = () => { /* transcript is best-effort; audio keeps recording */ };
      recog.onend = () => setInterim('');
      recogRef.current = recog;
      try { recog.start(); } catch { /* already started */ }
    }

    startedAtRef.current = Date.now();
    rec.start(1000);
    setRecording(true);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { stop(); return MAX_SECONDS; }
        return s + 1;
      });
    }, 1000);
  }, [onNote, stop]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={recording ? stop : () => void start()}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600,
          border: '1px solid var(--line)', cursor: disabled ? 'default' : 'pointer',
          background: recording ? 'var(--neg)' : 'var(--brand-3)',
          color: recording ? '#fff' : 'var(--bg-0, #0b0b0c)',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <span style={{ fontSize: 14 }}>{recording ? '■' : '●'}</span>
        {recording ? `Stop (${seconds}s)` : 'Record voice note'}
      </button>
      {recording && (
        <span style={{ fontSize: 12, color: 'var(--tx-3)', maxWidth: 360 }}>
          {interim || 'Listening…'}
        </span>
      )}
      {error && <span style={{ fontSize: 12, color: 'var(--warn)' }}>{error}</span>}
    </div>
  );
}
